import { CSSProperties, FC } from 'react';
import { Avatar, Button, Col, Divider, Row } from 'antd';
import {
    TbArrowUpRight,
    TbBriefcase,
    TbBuildingEstate,
    TbCalendarShare,
    TbClick,
    TbClockPin,
    TbCubePlus,
    TbId,
    TbSquareRoundedPlus2,
    TbWorldShare,
} from 'react-icons/tb';
import { useLocation } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { useRoutes } from '@/hooks/useRoutes';
import { StatusBadge } from '@/shared/components/badges';
import { CompactHoverCardIdentity, CompactHoverCardSurface } from '@/shared/components/cards';
import { SmallTag } from '@/shared/components/tags';
import { SmallTooltip } from '@/shared/components/tooltips';
import { ActivityPayload, usePresenceStore, useUserStore } from '@/stores';
import { MemberPermissionType } from '@/types';
import { corporateLogo, ellipsisStyling, formatDate, getLocalTime } from '@/utilities';

interface WorkspaceMemberHoverContentProps {
    activity?: ActivityPayload;
    collaborator: any;
    showResourceContext?: boolean;
    style?: CSSProperties;
}

const WorkspaceMemberHoverContent: FC<WorkspaceMemberHoverContentProps> = ({
    activity,
    collaborator,
    showResourceContext = false,
    style,
}): JSX.Element => {
    const { selectedUser: user } = useUserStore();
    const { token } = useAntDesignTheme();
    const { users } = usePresenceStore();
    const location = useLocation();
    const { getRouteMeta } = useRoutes();
    const {
        resourcePermissionMap,
        mapActivityToWorkspaceResourceStatus,
        mapStatusToWorkspaceStatus,
        workspacePermissionIconMap,
        resourcePermissionIconMap,
    } = useMaps();

    const member = collaborator?.member ?? collaborator;
    const collaboratorUser = member?.user ?? collaborator?.user;
    const status = collaboratorUser?.id ? users[collaboratorUser.id] : undefined;
    const workspacePermission = (member?.permission ?? collaborator?.permission ?? 'Member') as MemberPermissionType;
    const memberCreatedAt = member?.createdAt ?? collaborator?.createdAt;
    const meta = getRouteMeta(location.pathname);
    const displayName =
        [collaboratorUser?.firstName, collaboratorUser?.lastName].filter(Boolean).join(' ') ||
        collaboratorUser?.emailAddress ||
        'Workspace Member';

    return (
        <CompactHoverCardSurface
            style={{
                marginTop: '0.5rem',
                maxWidth: 16 * 20,
                minWidth: 16 * 20,
                padding: '0.5rem 0.675rem',
                ...ellipsisStyling,
                ...style,
            }}
        >
            <CompactHoverCardIdentity
                avatar={
                    <Avatar
                        size={36}
                        src={collaboratorUser?.avatar}
                        style={{
                            border: `1px solid ${token.colorBorder}`,
                        }}
                    >
                        {displayName.charAt(0)}
                    </Avatar>
                }
                subtitle={collaboratorUser?.emailAddress}
                title={displayName}
                trailing={collaboratorUser?.id === user?.id ? '(You)' : undefined}
            />
            <Divider style={{ margin: '0.5rem 0' }} />
            {showResourceContext && (
                <>
                    <Row style={{ marginTop: '0.5rem' }} align='middle' wrap={false}>
                        <Col flex='none'>
                            <SmallTag
                                styles={{ marginLeft: 0 }}
                                message={
                                    <span style={{ color: token.colorTextSecondary }}>
                                        <span style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }}>
                                            {resourcePermissionIconMap[collaborator?.permission as any]}
                                        </span>
                                        {meta?.handle?.singular ?? 'Resource'}{' '}
                                        {resourcePermissionMap[collaborator?.permission as any]}
                                    </span>
                                }
                            />
                        </Col>
                        <Col flex='auto' />
                        <Col flex='none'>
                            <StatusBadge
                                kind='activity'
                                status={activity?.type}
                                text={
                                    <small
                                        style={{
                                            fontWeight: 'normal',
                                            color: token.colorTextTertiary,
                                            verticalAlign: '1px',
                                            ...ellipsisStyling,
                                        }}
                                    >
                                        {activity?.type
                                            ? `${mapActivityToWorkspaceResourceStatus[activity?.type ?? 'idle']} ${meta?.handle?.singular ?? 'Resource'}`
                                            : `Not Active in ${meta?.handle?.singular ?? 'Resource'}`}
                                    </small>
                                }
                            />
                        </Col>
                    </Row>
                    <Row style={{ marginTop: '0.5rem' }} align='middle' wrap={false}>
                        <Col flex='none'>
                            <small
                                style={{
                                    fontWeight: 'normal',
                                    color: token.colorTextTertiary,
                                    ...ellipsisStyling,
                                }}
                            >
                                <TbCubePlus
                                    style={{ verticalAlign: '-1px', marginRight: '0.375rem' }}
                                    fill={token.colorBorder}
                                    stroke={token.colorTextTertiary}
                                />
                                {workspacePermission === 'Owner' ? 'Created' : 'Joined'}{' '}
                                {meta?.handle?.singular ?? 'Resource'} {memberCreatedAt && formatDate(memberCreatedAt)}
                            </small>
                        </Col>
                    </Row>
                    <Divider style={{ margin: '0.5rem 0' }} />
                </>
            )}
            <Row align='middle' wrap={false}>
                <Col flex='none'>
                    <SmallTag
                        styles={{ marginLeft: 0 }}
                        message={
                            <span style={{ color: token.colorTextSecondary }}>
                                <span style={{ verticalAlign: '-0.125rem', marginRight: '0.25rem' }}>
                                    {workspacePermissionIconMap[workspacePermission]}
                                </span>
                                Workspace {workspacePermission}
                            </span>
                        }
                    />
                </Col>
                <Col flex='auto' />
                <Col flex='none'>
                    <StatusBadge
                        status={status?.status ?? 'offline'}
                        text={
                            <small
                                style={{
                                    fontWeight: 'normal',
                                    color: token.colorTextTertiary,
                                    verticalAlign: '1px',
                                    ...ellipsisStyling,
                                }}
                            >
                                {mapStatusToWorkspaceStatus[status?.status ?? 'offline']}
                            </small>
                        }
                    />
                </Col>
            </Row>
            {status?.lastSeenAt && (
                <Row style={{ marginTop: '0.5rem' }} align='middle' wrap={false}>
                    <Col flex='none'>
                        <small
                            style={{
                                fontWeight: 'normal',
                                color: token.colorTextTertiary,
                                ...ellipsisStyling,
                            }}
                        >
                            <TbClick
                                style={{ verticalAlign: '-1px', marginRight: '0.375rem' }}
                                fill={token.colorBorder}
                                stroke={token.colorTextTertiary}
                            />
                            Last Seen {formatDate(new Date(status.lastSeenAt))}
                        </small>
                    </Col>
                </Row>
            )}
            {collaboratorUser?.timeZone && (
                <Row style={{ marginTop: '0.5rem' }} align='middle' wrap={false}>
                    <Col flex='none'>
                        <small
                            style={{
                                fontWeight: 'normal',
                                color: token.colorTextTertiary,
                                ...ellipsisStyling,
                            }}
                        >
                            <TbClockPin
                                fill={token.colorBorder}
                                stroke={token.colorTextTertiary}
                                style={{ verticalAlign: '-1px', marginRight: '0.375rem' }}
                            />
                            <span style={{ fontWeight: 500 }}>{getLocalTime(collaboratorUser.timeZone)}</span> Local
                            Time
                        </small>
                    </Col>
                </Row>
            )}
            {collaboratorUser?.companyWebsite && (
                <Row style={{ marginTop: '0.5rem' }} align='middle' wrap={false}>
                    <Col flex='none'>
                        <small
                            style={{
                                fontWeight: 'normal',
                                color: token.colorTextTertiary,
                                ...ellipsisStyling,
                            }}
                        >
                            <Avatar
                                shape='square'
                                size={16}
                                style={{
                                    border: `1px solid ${token.colorBorder}`,
                                    backgroundColor: token.colorBgContainer,
                                    verticalAlign: '-0.175rem',
                                    marginRight: '0.25rem',
                                }}
                                src={corporateLogo(`https://${collaboratorUser.companyWebsite}`)}
                                icon={
                                    <TbBuildingEstate
                                        size='67.5%'
                                        fill={token.colorBorder}
                                        stroke={token.colorTextTertiary}
                                        style={{ verticalAlign: '-1px', marginRight: '0.375rem' }}
                                    />
                                }
                            />
                            <span style={{ fontWeight: 500 }}>{collaboratorUser?.companyName}</span>
                        </small>
                    </Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        <a
                            href={`https://${collaboratorUser.companyWebsite}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }}
                        >
                            <TbWorldShare />
                        </a>
                    </Col>
                </Row>
            )}
            {collaboratorUser?.title && (
                <Row style={{ marginTop: '0.5rem' }} align='middle' wrap={false}>
                    <Col flex='none'>
                        <small
                            style={{
                                fontWeight: 'normal',
                                color: token.colorTextTertiary,
                                ...ellipsisStyling,
                            }}
                        >
                            <TbBriefcase
                                fill={token.colorBorder}
                                stroke={token.colorTextTertiary}
                                style={{ verticalAlign: '-1px', marginRight: '0.375rem' }}
                            />
                            <span style={{ fontWeight: 400 }}>{collaboratorUser.title}</span>
                        </small>
                    </Col>
                </Row>
            )}
            {collaboratorUser?.bio && (
                <Row style={{ marginTop: '0.5rem' }} align='middle'>
                    <Col flex='auto'>
                        <SmallTooltip title={collaboratorUser.bio} placement='rightTop' padding='0 1rem'>
                            <small
                                style={{
                                    fontWeight: 'normal',
                                    color: token.colorTextTertiary,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    whiteSpace: 'normal',
                                }}
                            >
                                <TbId
                                    fill={token.colorBorder}
                                    stroke={token.colorTextTertiary}
                                    style={{ verticalAlign: '-1px', marginRight: '0.375rem' }}
                                />
                                <span style={{ fontWeight: 400 }}>{collaboratorUser.bio}</span>
                            </small>
                        </SmallTooltip>
                    </Col>
                </Row>
            )}
            <Row style={{ marginTop: '0.5rem' }} align='middle' wrap={false}>
                <Col flex='none'>
                    <small
                        style={{
                            fontWeight: 'normal',
                            color: token.colorTextTertiary,
                            ...ellipsisStyling,
                        }}
                    >
                        <TbSquareRoundedPlus2
                            style={{ verticalAlign: '-1px', marginRight: '0.375rem' }}
                            fill={token.colorBorder}
                            stroke={token.colorTextTertiary}
                        />
                        {workspacePermission === 'Owner' ? 'Created' : 'Joined'} Workspace{' '}
                        {memberCreatedAt && formatDate(memberCreatedAt)}
                    </small>
                </Col>
            </Row>
            {collaboratorUser?.calendarLink && (
                <>
                    <Divider style={{ margin: '0.5rem 0' }} />
                    <Button
                        block
                        size='small'
                        onClick={() => {
                            window.open(collaboratorUser.calendarLink, '_blank');
                        }}
                        type='dashed'
                    >
                        <Row align='middle' wrap={false} style={{ width: '100%' }}>
                            <Col flex='none'>
                                <TbCalendarShare style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }} />
                                Schedule a Call with {collaboratorUser?.firstName}
                            </Col>
                            <Col flex='auto' />
                            <Col flex='none'>
                                <TbArrowUpRight
                                    style={{ verticalAlign: '-0.125rem', color: token.colorTextTertiary }}
                                />
                            </Col>
                        </Row>
                    </Button>
                </>
            )}
        </CompactHoverCardSurface>
    );
};

export default WorkspaceMemberHoverContent;
